import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { logout } from '../services/authService';
import SignInModal from './SignInModal';
import './Nav.css';

const Nav = () => {
    const [hoveredItem, setHoveredItem] = useState(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isProfileOpen, setIsProfileOpen] = useState(false);
    const { currentUser } = useAuth();
    const navigate = useNavigate();
    const profileRef = useRef(null);

    const handleLogout = async () => {
        await logout();
        setIsProfileOpen(false);
        navigate('/');
    };

    // Close profile dropdown when clicking outside
    useEffect(() => {
        function handleClickOutside(e) {
            if (profileRef.current && !profileRef.current.contains(e.target)) {
                setIsProfileOpen(false);
            }
        }

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const navVariants = {
        hidden: { y: -100, opacity: 0 },
        visible: {
            y: 0,
            opacity: 1,
            transition: {
                type: 'spring',
                stiffness: 100,
                damping: 20,
                staggerChildren: 0.1,
            },
        },
    };

    const itemVariants = {
        hidden: { y: -20, opacity: 0 },
        visible: {
            y: 0,
            opacity: 1,
            transition: {
                type: 'spring',
                stiffness: 300,
                damping: 24,
            },
        },
    };

    const buttonVariants = {
        rest: { scale: 1 },
        hover: {
            scale: 1.05,
            transition: {
                type: 'spring',
                stiffness: 400,
                damping: 10,
            },
        },
        tap: { scale: 0.95 },
    };

    const underlineVariants = {
        hidden: { width: 0, opacity: 0 },
        visible: {
            width: '100%',
            opacity: 1,
            transition: {
                type: 'spring',
                stiffness: 300,
                damping: 20,
            },
        },
    };

    const dropdownVariants = {
        hidden: {
            opacity: 0,
            scale: 0.95,
            y: -10,
        },
        visible: {
            opacity: 1,
            scale: 1,
            y: 0,
            transition: {
                type: 'spring',
                stiffness: 300,
                damping: 25,
            },
        },
        exit: {
            opacity: 0,
            scale: 0.95,
            y: -10,
            transition: {
                duration: 0.2,
            },
        },
    };

    return (
        <>
            <motion.nav
                className="nav-container"
                variants={navVariants}
                initial="hidden"
                animate="visible"
            >
                <motion.div className="nav-content" variants={itemVariants}>
                    {/* Logo/Brand */}
                    <Link to="/" style={{ textDecoration: 'none' }}>
                        <motion.div
                            className="nav-brand"
                            whileHover={{ scale: 1.05 }}
                            whileTap={{ scale: 0.95 }}
                        >
                            <motion.div
                                className="brand-icon"
                                animate={{
                                    rotate: [0, 5, -5, 0],
                                }}
                                transition={{
                                    duration: 2,
                                    repeat: Infinity,
                                    repeatDelay: 3,
                                }}
                            >
                                ✨
                            </motion.div>
                            <span className="brand-text">Danica Halley</span>
                        </motion.div>
                    </Link>

                    {/* Navigation Items */}
                    <motion.div className="nav-items" variants={itemVariants}>
                        <motion.div
                            className="nav-item"
                            onHoverStart={() => setHoveredItem('home')}
                            onHoverEnd={() => setHoveredItem(null)}
                            variants={buttonVariants}
                            initial="rest"
                            whileHover="hover"
                            whileTap="tap"
                        >
                            <Link to="/" className="nav-link">
                                <motion.span className="nav-link-text">Home</motion.span>
                                {hoveredItem === 'home' && (
                                    <motion.div
                                        className="nav-underline"
                                        variants={underlineVariants}
                                        initial="hidden"
                                        animate="visible"
                                        layoutId="underline"
                                    />
                                )}
                            </Link>
                        </motion.div>

                        {currentUser ? (
                            // Profile Dropdown when logged in
                            <div
                                className="profile-container"
                                ref={profileRef}
                                onMouseEnter={() => setIsProfileOpen(true)}
                                onMouseLeave={() => setIsProfileOpen(false)}
                            >
                                <motion.button
                                    className="profile-button"
                                    variants={buttonVariants}
                                    initial="rest"
                                    whileHover="hover"
                                    whileTap="tap"
                                    onClick={() => setIsProfileOpen(prev => !prev)}
                                    onHoverStart={() => setHoveredItem('profile')}
                                    onHoverEnd={() => setHoveredItem(null)}
                                >
                                    <motion.div className="profile-avatar">
                                        {currentUser.email?.charAt(0).toUpperCase()}
                                    </motion.div>
                                    <motion.div
                                        className="profile-glow"
                                        animate={{
                                            opacity: hoveredItem === 'profile' ? 1 : 0,
                                        }}
                                        transition={{ duration: 0.3 }}
                                    />
                                </motion.button>

                                <AnimatePresence>
                                    {isProfileOpen && (
                                        <motion.div
                                            className="profile-dropdown"
                                            variants={dropdownVariants}
                                            initial="hidden"
                                            animate="visible"
                                            exit="exit"
                                        >
                                            <div className="profile-dropdown-header">
                                                <div className="profile-dropdown-avatar">
                                                    {currentUser.email?.charAt(0).toUpperCase()}
                                                </div>
                                                <div className="profile-dropdown-info">
                                                    <p className="profile-dropdown-email">
                                                        {currentUser.email}
                                                    </p>
                                                </div>
                                            </div>
                                            <div className="profile-dropdown-divider" />
                                            <motion.button
                                                className="profile-dropdown-logout"
                                                onClick={handleLogout}
                                                whileHover={{ backgroundColor: '#f7fafc' }}
                                                whileTap={{ scale: 0.98 }}
                                            >
                                                <span className="logout-icon">🚪</span>
                                                Sign Out
                                            </motion.button>
                                        </motion.div>
                                    )}
                                </AnimatePresence>
                            </div>
                        ) : (
                            // Sign In button when not logged in
                            <motion.button
                                className="signin-button"
                                variants={buttonVariants}
                                initial="rest"
                                whileHover="hover"
                                whileTap="tap"
                                onHoverStart={() => setHoveredItem('signin')}
                                onHoverEnd={() => setHoveredItem(null)}
                                onClick={() => setIsModalOpen(true)}
                            >
                                <motion.span className="button-text">Sign In</motion.span>
                                <motion.div
                                    className="button-glow"
                                    animate={{
                                        opacity: hoveredItem === 'signin' ? 1 : 0,
                                        scale: hoveredItem === 'signin' ? 1 : 0.8,
                                    }}
                                    transition={{ duration: 0.3 }}
                                />
                                <motion.div
                                    className="button-shine"
                                    animate={{
                                        x: hoveredItem === 'signin' ? '200%' : '-100%',
                                    }}
                                    transition={{ duration: 0.6 }}
                                />
                            </motion.button>
                        )}
                    </motion.div>
                </motion.div>

                {/* Animated background blur effect */}
                <motion.div
                    className="nav-backdrop"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 0.5 }}
                />
            </motion.nav>

            <SignInModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} />
        </>
    );
};

export default Nav;

